/**
 * Staging — furnish a room with the props of its space type's kit.
 *
 * Pure: a plan and a room in, a list of placed props out. Deterministic for a
 * given seed (the estimate's guid, say), so the same estimate always stages the
 * same way.
 *
 * Coordinates: a FLOOR prop is placed in room coordinates (x along the room's
 * length, z along its width, from the front-left corner, as the plan has them),
 * with `rotation` the yaw that turns its back to the wall it stands against
 * (0 = back to the front wall, i.e. facing +z). A HUNG or WALL prop is placed on
 * a wall `face`, at `x` along that wall's own surface coordinate (the same the
 * wall's sections and openings use) and `y` up from the floor — for a hung
 * prop, the point its accessory holds it at; for a wall prop, the prop itself.
 *
 * Rules:
 *   - nothing on the floor stands in a door's swing, in a garage door's path
 *     (its width, as deep into the room as it is tall, where the open door
 *     hangs), in front of a removed shared wall, or against a window it
 *     reaches up to (a washer stands under a high window);
 *   - a floor prop keeps its `clearance` free in front of it — when it is
 *     placed, and after: nothing placed later stands in that room (two props'
 *     clearances may overlap each other) — and props do not overlap each
 *     other. Two that stand `besides` each other (the washer and
 *     dryer) stand on one wall a hand apart: the first takes a spot with room
 *     for its partner, and when no wall has such a spot with their clearances
 *     clear, the room they keep in front may lie over a door's swing (never
 *     their bodies) — together over a swing beats split across the room;
 *   - a hung prop hangs on a SlatWall ACCESSORY (data/slatwallAccessories.ts)
 *     of a kind it can hang from — one the USER put on the wall, each taking
 *     one prop. Only when the user has put no accessory on any wall of the
 *     plan does staging hang its own, as `accessory` props of the staging
 *     (gone with Clear Staging, never counted), on a SlatWall section, on the
 *     slot pitch. Either way the hung item stays inside the panelled wall, off
 *     any window or door, clear of other hung items, and above any floor prop
 *     standing under it;
 *   - a wall prop goes on panelled wall (any product, across the seam between
 *     two — wall art over the SlatWall band and the boards above it), off
 *     openings, CENTRED in the free run of wall at its height (between the
 *     wall's ends, the openings, tall floor props and other hung things);
 *     several copies are spread evenly along one run;
 *   - a prop hung from the CEILING hangs over open floor, a foot and a half off
 *     the walls, with walking room under it (CEILING_HEADROOM), not over a
 *     door's swing or a garage door's path, nor over a floor prop that reaches
 *     up into it, and apart from the other ceiling props;
 *   - a floor prop with `anchor: 'bay'` (a car) stands in line with a garage
 *     door, one a door, facing in or out, all or part of it in the room — the
 *     rest outside through the door, which the designer then holds open;
 *   - a floor prop with `anchor: 'corner'` (a shower) stands in a corner of
 *     the room (the seed's choice), tight to both walls: its back to one, its
 *     left side to the other, its side clearance kept on the open side only.
 *     A corner needs both its walls; with no corner free it is left out,
 *     never put elsewhere.
 *
 * A prop can go more than one way (catalogue.ts `places`, best first: boots on
 * a shelf, else on the floor): each is tried in turn until one fits, and the
 * staged prop says which (`place`).
 */

import { Opening, Section, Surface, doorSwing, sectionFillMode } from '../../types/sections';
import { SlatWallAccessoryKind, accessoryDef } from '../../data/slatwallAccessories';
import { accessoryRect, seatPositions } from '../sections/accessories';
import { FloorPlan, PlanFace, PlanRoom, PLAN_FACES, StagedProp, findRoom, sheetAlong, surfaceRange, wallIsGone } from '../plan/floorPlan';
import { PropDef, StagingKit, PropSize, Placement } from './kits';
import { PLACEMENT_ACCESSORY, dropOn, isAccessoryPlace, placementOf, placesOf } from './catalogue';
import { ceilingGeometry } from '../ceiling/profile';
import { yardOf } from '../plan/yard';

export type { StagedProp } from '../plan/floorPlan';
export { pruneStaging } from '../plan/floorPlan';

/** An axis-aligned rectangle on the floor, room coordinates. */
/**
 * `sill`: a window's strip keeps out only what reaches up to the window (its bottom edge, feet).
 * `swing`: a door's swing — a pair that cannot otherwise stand together may keep its room in front over it.
 */
interface FloorRect { x0: number; z0: number; x1: number; z1: number; sill?: number; swing?: true }
/** A span on a wall, surface coordinates. */
interface WallRect { x0: number; x1: number; y0: number; y1: number }

const EPS = 1e-6;
const STEP = 0.5;          // feet, along a wall
const WALL_GAP = 0.05;     // a prop stands this far off the wall face
const PAIR_GAP = 1 / 12;   // a prop stands this far from the partner it is placed beside (a dryer by the washer)
const SEAM_GAP = 0.25;     // the most two stacked sections may be apart (a trim between them) for a wall prop to hang across them
const WALL_END_MARGIN = 0.2; // a wall prop keeps this far from the wall's ends (feet)
const SPREAD_GAP = 1;      // the least room between wall props spread along one run of wall (feet)
const MAX_SPREAD = 6;      // the most copies spread along one run
/** The least room under a prop hung from the ceiling (feet): 6' 9". */
export const CEILING_HEADROOM = 6.75;
/** How far a ceiling prop keeps off the walls, and off another ceiling prop (feet). */
const CEILING_OFF_WALL = 1.5;
const CEILING_GAP = 0.8;

/** mulberry32: small, good enough, deterministic. (Exported for the render finish's draw, renderFinish.ts.) */
export function rng(seed: string): () => number {
  let h = 1779033703 ^ seed.length;
  for (let i = 0; i < seed.length; i++) h = Math.imul(h ^ seed.charCodeAt(i), 3432918353), h = (h << 13) | (h >>> 19);
  let a = (h ^ (h >>> 16)) >>> 0;
  return () => {
    a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** A list in a seeded order. */
function shuffleWith<T>(random: () => number, list: T[]): T[] {
  const a = [...list];
  for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(random() * (i + 1)); [a[i], a[j]] = [a[j], a[i]]; }
  return a;
}

const overlaps = (a: FloorRect, b: FloorRect) => a.x0 < b.x1 - EPS && a.x1 > b.x0 + EPS && a.z0 < b.z1 - EPS && a.z1 > b.z0 + EPS;
const sameFloorRect = (a: FloorRect, b: FloorRect) => Math.abs(a.x0 - b.x0) < EPS && Math.abs(a.x1 - b.x1) < EPS && Math.abs(a.z0 - b.z0) < EPS && Math.abs(a.z1 - b.z1) < EPS;
const overlapsW = (a: WallRect, b: WallRect) => a.x0 < b.x1 - EPS && a.x1 > b.x0 + EPS && a.y0 < b.y1 - EPS && a.y1 > b.y0 + EPS;

/** How far in front of the prop it faces a prop stands (feet). */
const FACING_GAP = 0.5;
/** How far outside a closed garage door a car on the driveway stands (feet), from the wall's inside face. */
const DRIVE_STANDOFF = 1.5;

/** The yaw at which a prop standing at (x, z) faces the point (tx, tz): 0 faces +z. */
export function facingFrom(x: number, z: number, tx: number, tz: number): number {
  return Math.atan2(tx - x, tz - z);
}

/** A vector of the prop's frame (x along, z into the room) turned by its yaw, as the viewer turns the prop (a rotation about y). */
function turned(px: number, pz: number, yaw: number): { x: number; z: number } {
  const c = Math.cos(yaw), s = Math.sin(yaw);
  return { x: px * c + pz * s, z: -px * s + pz * c };
}

/** The yaw that turns a prop's back to `face`. */
export function rotationFor(face: PlanFace): number {
  switch (face) {
    case 'front': return 0;
    case 'back': return Math.PI;
    case 'left': return Math.PI / 2;
    case 'right': return -Math.PI / 2;
  }
}

/**
 * The floor rectangle of a prop standing with its back to `face`, its centre
 * `along` the wall (sheet position) and `out` feet deep (size.d, or that plus
 * clearance), in room coordinates.
 */
function footprintAgainst(room: PlanRoom, face: PlanFace, along: number, w: number, out: number, gap = WALL_GAP): FloorRect {
  const a = along - room.x, b = along - room.z; // along the wall, in room coordinates
  switch (face) {
    case 'front': return { x0: a - w / 2, x1: a + w / 2, z0: gap, z1: gap + out };
    case 'back': return { x0: a - w / 2, x1: a + w / 2, z0: room.width - gap - out, z1: room.width - gap };
    case 'left': return { x0: gap, x1: gap + out, z0: b - w / 2, z1: b + w / 2 };
    case 'right': return { x0: room.length - gap - out, x1: room.length - gap, z0: b - w / 2, z1: b + w / 2 };
  }
}

/** A point `along` the wall and `out` from it (the prop's frame: x along, z into the room) in room coordinates, for a prop with its back to `face`. */
function fromWall(room: PlanRoom, face: PlanFace, along: number, out: number): { x: number; z: number } {
  const a = along - room.x, b = along - room.z;
  switch (face) {
    case 'front': return { x: a, z: out };
    case 'back': return { x: a, z: room.width - out };
    case 'left': return { x: out, z: b };
    case 'right': return { x: room.length - out, z: b };
  }
}

/** Where along a wall (sheet coordinate) a surface-x is, and the wall's sheet span. */
function wallSpan(room: PlanRoom, face: PlanFace): [number, number] {
  return face === 'front' || face === 'back' ? [room.x, room.x + room.length] : [room.z, room.z + room.width];
}

const wallOf = (room: PlanRoom, face: PlanFace): Surface => room.surfaces.walls[PLAN_FACES.indexOf(face)];

/** A garage door of the room, as a bay a car stands in line with: its wall, its middle along it (sheet coordinate), and the floor it claims. */
export interface GarageBay { face: PlanFace; opening: Opening; along: number; keepOut: FloorRect }

/** The floor a garage door claims: its width and a little, as deep into the room as the open door hangs — a car sits under it. */
function bayKeepOut(room: PlanRoom, face: PlanFace, o: Opening, along: number): FloorRect {
  return footprintAgainst(room, face, along, o.width + 0.5, o.height + 1.5);
}

/** The room's garage doors (on walls still there), as bays. */
export function garageBays(plan: FloorPlan, room: PlanRoom): GarageBay[] {
  const out: GarageBay[] = [];
  for (const face of PLAN_FACES) {
    if (wallIsGone(plan, room, face)) continue;
    for (const o of wallOf(room, face).openings ?? []) {
      if (o.kind !== 'garageDoor') continue;
      const along = (sheetAlong(room, face, o.x) + sheetAlong(room, face, o.x + o.width)) / 2;
      out.push({ face, opening: o, along, keepOut: bayKeepOut(room, face, o, along) });
    }
  }
  return out;
}

/** The floor the room's openings claim: door swings, garage-door paths, passages, under windows. */
export function floorKeepOuts(plan: FloorPlan, room: PlanRoom): FloorRect[] {
  const out: FloorRect[] = [];
  for (const face of PLAN_FACES) {
    const wall = wallOf(room, face);
    for (const o of wall.openings ?? []) {
      const a0 = sheetAlong(room, face, o.x), a1 = sheetAlong(room, face, o.x + o.width);
      const lo = Math.min(a0, a1), hi = Math.max(a0, a1);
      const mid = (lo + hi) / 2;
      let depth: number;
      let w = hi - lo;
      if (o.kind === 'door') {
        // The leaf swings into the room: a square the width of the door, from
        // the hinge side (only when it opens in; opening out, just a step clear).
        depth = doorSwing(o) === 'in' ? o.width + 0.3 : 1;
        w = o.width + 0.6;
        out.push({ ...footprintAgainst(room, face, mid, w, depth), swing: true });
        continue;
      } else if (o.kind === 'garageDoor') {
        out.push(bayKeepOut(room, face, o, mid)); // the same rectangle a car in its bay is allowed into
        continue;
      } else if (o.kind === 'passage') {
        depth = 3; // a way through
        w = o.width + 0.5;
      } else {
        // A window: nothing that reaches up to it stands tight against it. Something lower (a washer under a high window) may.
        out.push({ ...footprintAgainst(room, face, mid, o.width + 0.5, 0.6), sill: o.y });
        continue;
      }
      out.push(footprintAgainst(room, face, mid, w, depth));
    }
  }
  return out;
}

/**
 * The parts of a wall (surface coordinates) a hung or wall prop must stay off:
 * its openings, and the garage-door opener's wall button beside a garage door
 * (where room3d/openingMeshBuilder puts it: 8″ past the door on whichever side
 * has wall, at 4′ 6″).
 */
export function wallKeepOuts(wall: Surface): WallRect[] {
  const out: WallRect[] = (wall.openings ?? []).map((o: Opening) => ({ x0: o.x - 0.15, x1: o.x + o.width + 0.15, y0: o.y - 0.15, y1: o.y + o.height + 0.15 }));
  for (const o of wall.openings ?? []) {
    if (o.kind !== 'garageDoor') continue;
    const bw = 2.75 / 12, bh = 4.5 / 12;
    const x1 = o.x + o.width;
    const right = x1 + 8 / 12 + bw <= wall.width - 2 / 12;
    const bx0 = right ? x1 + 8 / 12 : o.x - 8 / 12 - bw;
    const by0 = o.y + 4.5;
    out.push({ x0: bx0 - 0.5, x1: bx0 + bw + 0.5, y0: by0 - 0.5, y1: by0 + bh + 0.5 });
  }
  return out;
}

/**
 * Which wall meets which at each corner, and at which end of each (surface x
 * = 0 or = the wall's length): front(0)–left(0), front(L)–right(W),
 * right(0)–back(0), back(L)–left(W). A floor prop standing in a corner on one
 * wall also takes up the start of the other.
 */
const CORNERS: [PlanFace, 'start' | 'end', PlanFace, 'start' | 'end'][] = [
  ['front', 'start', 'left', 'start'],
  ['front', 'end', 'right', 'end'],
  ['right', 'start', 'back', 'start'],
  ['back', 'end', 'left', 'end'],
];

/** The SlatWall sections of a wall (what accessories can go on). */
function slatSections(wall: Surface): Section[] {
  return wall.sections.filter((s) => sectionFillMode(s.productType) === 'insert');
}

/** Does any wall of any room carry an accessory the user hung? */
export function planHasUserAccessories(plan: FloorPlan): boolean {
  return plan.rooms.some((r) => r.surfaces.walls.some((w) => w.sections.some((s) => (s.accessories?.length ?? 0) > 0)));
}

/**
 * The rectangle a hung item takes on the wall, held at (hx, hy): `w` wide,
 * from `drop` below the hold point to the rest of its height above it (or
 * standing on / in the accessory when drop is 0), and the accessory's own
 * rectangle with it.
 */
function hungRect(prop: PropDef, hx: number, hy: number, acc: WallRect, drop = prop.drop ?? 0): WallRect {
  // A hanging item reaches at least a few inches above the point that holds
  // it (its head over the prong), whatever its drop says.
  const itemTop = drop > 0 ? hy + Math.max(0.3, prop.size.h - drop) : hy + prop.size.h;
  const itemBottom = drop > 0 ? hy - drop : hy;
  // The hold point `holdX` right of the box's centre: the box reaches that much further left of it.
  const hold = prop.holdX ?? 0;
  const left = prop.reach?.left ?? prop.size.w / 2 + hold;
  const right = prop.reach?.right ?? prop.size.w / 2 - hold;
  return {
    x0: Math.min(hx - left, acc.x0), x1: Math.max(hx + right, acc.x1),
    y0: Math.min(itemBottom, acc.y0), y1: Math.max(itemTop, acc.y1),
  };
}

/**
 * One of a prop's photoreal models, as staging needs it: its library key, the
 * size it is staged at, and where its placed box sits in the prop's frame —
 * `offset` [along, into] of the box's centre from the prop's origin (a floor
 * model placed off-centre), `reach` left and right of the hold point and
 * `drop` below it (a hung model), so the room is kept clear where the model
 * really is, not where the kit's box was.
 */
export interface StageVariant {
  key: string;
  size: PropSize;
  offset?: [number, number];
  reach?: { left: number; right: number };
  drop?: number;
  /** At most this many of this model on one wall (hung, wall) or in one room (floor, ceiling); absent: no limit. */
  perWall?: number;
}

/** A staged copy's size and, when the library was asked, which model it was staged for and where its box sits. */
interface Pick { size: PropSize; variant?: string; offset?: [number, number]; reach?: { left: number; right: number }; drop?: number }

export interface StageOptions {
  /** Deterministic placement: the same seed gives the same room. */
  seed?: string;
  /**
   * The photoreal models Prop Studio has placed for each prop (by prop id),
   * each with the size it is staged at. Every copy of a prop that has any is
   * staged for ONE of them, drawn by the seed, and says which (`variant`):
   * a FLOOR or CEILING copy takes that model's size in place of the kit's,
   * so what the renderer puts there is what the room was staged round —
   * neighbours, clearances and corners are kept for the model's real size,
   * not the stylised prop's. Hung props keep the kit's size (their models
   * hang from the same point) but still say which model is theirs.
   */
  variants?: Record<string, StageVariant[]>;
  /**
   * How full the room is (default 1: each prop as many times as its kit says).
   * The main pieces (`order` below MAIN_ORDER: the washer, the workbench, the
   * vanity) are placed once whatever the density. The rest — bins, a hamper,
   * the tools on the wall — are thinned below 1 (each copy kept with that
   * chance; none at 0) and multiplied above it (2 = twice as many, the
   * fraction decided by the seed), up to MAX_DENSITY.
   */
  density?: number;
}

/** Props ordered before this are a space's main pieces: one each, at any density. */
export const MAIN_ORDER = 30;
export const MAX_DENSITY = 3;

/** How many times a room is staged over for a main piece that found no room (stageRoom). */
export const MAX_ATTEMPTS = 4;
/** The most placements the main pieces' search may try in one staging (stageRoomOnce). */
export const SEARCH_BUDGET = 6000;

/**
 * Stage one room with a kit. Props that find no place are left out — but a
 * MAIN PIECE (a floor prop ordered below MAIN_ORDER: the vanity, the toilet,
 * a shower) that finds none is given another go: it is promoted to the front
 * of the order and the room is staged again, on a fresh draw, up to
 * MAX_ATTEMPTS times, and the attempt that seats the most main pieces is
 * kept (the first, when they all fit — so a room where everything fits is
 * staged exactly as it always was). Placing is greedy, and a piece placed
 * earlier can take the one spot a later piece needed — a toilet standing
 * mid-wall with its side clearance can close both corners to the shower —
 * while a different order or draw seats them all.
 */
export function stageRoom(plan: FloorPlan, roomId: string, kit: StagingKit, options: StageOptions = {}): StagedProp[] {
  const main = kit.props.filter((p) => p.kind === 'floor' && !p.disabled && (p.order ?? 50) < MAIN_ORDER && p.anchor !== 'bay');
  const early: string[] = [];
  let best: { staged: StagedProp[]; missing: number } | null = null;
  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
    const seed = attempt === 0 ? options.seed : `${options.seed ?? 'stage'}:again${attempt}`;
    const staged = stageRoomOnce(plan, roomId, kit, { ...options, seed }, early);
    const missing = main.filter((p) => !staged.some((s) => s.propId === p.id));
    if (!best || missing.length < best.missing) best = { staged, missing: missing.length };
    if (missing.length === 0) break;
    for (const p of missing) if (!early.includes(p.id)) early.push(p.id);
  }
  return best?.staged ?? [];
}

/** One staging of a room: `early` are the main pieces promoted to the front of the order (after any car in its bay). */
function stageRoomOnce(plan: FloorPlan, roomId: string, kit: StagingKit, options: StageOptions, early: string[]): StagedProp[] {
  const room = findRoom(plan, roomId);
  if (!room) return [];
  const random = rng(`${options.seed ?? 'stage'}:${roomId}`);
  /**
   * What this copy of a prop is staged as: one of its photoreal models when
   * Prop Studio has placed any (StageOptions.variants) — drawn here, so the
   * seed fixes it — at that model's size for a floor or ceiling prop; else
   * the kit's size and no model.
   */
  const pickFor = (p: PropDef, allowed?: (v: StageVariant) => boolean): Pick | null => {
    const list = options.variants?.[p.id];
    if (!list?.length) return { size: p.size };
    const pool = allowed ? list.filter(allowed) : list;
    if (!pool.length) return null; // every model at its limit here: this copy is left out
    const v = pool[Math.min(pool.length - 1, Math.floor(random() * pool.length))];
    const standing = p.kind === 'floor' || p.kind === 'ceiling' || p.kind === 'yard';
    // A held copy keeps the kit's width (its reach says where it really is) but takes the model's height, so the band it holds on the wall is the model's.
    return { size: standing ? v.size : v.reach ? { ...p.size, h: v.size.h } : p.size, variant: v.key, offset: standing ? v.offset : undefined, reach: standing ? undefined : v.reach, drop: standing ? undefined : v.drop };
  };
  /**
   * How many of each model stand in this room (floor, ceiling) or hang on
   * each wall: a model's `perWall` caps them, so two identical boots are not
   * hung side by side when Prop Studio said one a wall. A hung copy picked
   * before its wall is known is picked again for a wall where its model is
   * at the limit (`repickOn`), among the models still under theirs.
   */
  const modelCounts = new Map<string, number>();
  const countKey = (key: string, face?: PlanFace) => `${key}@${face ?? 'room'}`;
  const underCap = (v: StageVariant, face?: PlanFace) => v.perWall === undefined || (modelCounts.get(countKey(v.key, face)) ?? 0) < v.perWall;
  const countOne = (variant: string | undefined, face?: PlanFace) => {
    if (!variant) return;
    modelCounts.set(countKey(variant, face), (modelCounts.get(countKey(variant, face)) ?? 0) + 1);
  };
  const variantOf = (p: PropDef, key: string | undefined) => (key ? options.variants?.[p.id]?.find((v) => v.key === key) : undefined);
  const repickOn = (p: PropDef, pick: Pick, face: PlanFace): Pick | null => {
    const v = variantOf(p, pick.variant);
    if (!v || underCap(v, face)) return pick;
    return pickFor(p, (w) => underCap(w, face));
  };
  /** A hung prop as its model reaches: the model's reach and drop in place of the kit's. */
  const hungAs = (p: PropDef, pick: Pick): PropDef => (pick.reach ? { ...p, reach: pick.reach, ...(pick.drop !== undefined ? { drop: pick.drop } : {}), size: { ...p.size, h: Math.max(pick.size.h, (pick.drop ?? 0) + 0.3) } } : p);
  const placed: StagedProp[] = [];
  const taken: FloorRect[] = [...floorKeepOuts(plan, room)];
  /**
   * The room each floor prop keeps clear in front of it (its `clearance`),
   * once it stands: no later prop's body goes there — the toilet does not
   * stand in front of the shower's door. Two props' clearances may overlap
   * each other (the vanity and the toilet share the room in front of them),
   * which is why these are kept apart from `taken`.
   */
  const clearances: FloorRect[] = [];
  const inClearance = (rect: FloorRect) => clearances.some((c) => overlaps(c, rect));
  /**
   * Is a thing `h` tall, over `rect`, in something taken — or in a window's
   * strip it reaches up to? `overSwing`: a door's swing does not count (for
   * the room a pair keeps in front, never for a body).
   */
  const blockedBy = (rect: FloorRect, h: number, overSwing = false) => taken.some((t) => overlaps(t, rect) && (t.sill === undefined || h + 0.2 > t.sill) && !(overSwing && t.swing));
  // The garage doors, each a bay one car can stand in line with.
  const bays = garageBays(plan, room);
  const usedBays = new Set<string>();
  // Floor props by wall, surface x: `x0`–`x1` with the side clearance they keep (for the floor), `bx0`–`bx1` the body alone (for what hangs above).
  const standing: { face: PlanFace; x0: number; x1: number; bx0: number; bx1: number; h: number; d: number }[] = [];
  // How a prop that faces another may stand when that one is in the room (placeFloor): at its full
  // distance only, that or nearer, or (`any`) also as its anchor says when nothing in front fits.
  // The main pieces' search sets it (seatMainPieces); outside the search anything goes.
  let facingMode: 'full' | 'near' | 'any' = 'any';
  // Where each prop (by id) was stood against a wall, for a partner to stand beside it (or in front, facing it): `out` is how far its front is off the wall.
  const againstWall = new Map<string, { face: PlanFace; along: number; w: number; out: number }>();
  /** What stands under a wall's hung props: its own floor props, and corner neighbours reaching round. */
  const standingOn = (face: PlanFace, wallLength: number) => {
    const own = standing.filter((s) => s.face === face);
    const round: typeof own = [];
    for (const [a, aEnd, b, bEnd] of CORNERS) {
      const pairs: [PlanFace, 'start' | 'end', PlanFace, 'start' | 'end'][] = [[a, aEnd, b, bEnd], [b, bEnd, a, aEnd]];
      for (const [here, hereEnd, other, otherEnd] of pairs) {
        if (here !== face) continue;
        const otherWall = wallOf(room, other);
        for (const s of standing) {
          if (s.face !== other) continue;
          const inCorner = otherEnd === 'start' ? s.x0 < 0.3 : s.x1 > otherWall.width - 0.3;
          if (!inCorner) continue;
          // Its body runs along this wall from the hand's gap it keeps off the other, by its depth (`bx`); with a little over for the floor (`x`).
          round.push(hereEnd === 'start'
            ? { ...s, face, x0: 0, x1: s.d + 0.2, bx0: WALL_GAP, bx1: WALL_GAP + s.d }
            : { ...s, face, x0: wallLength - s.d - 0.2, x1: wallLength, bx0: wallLength - WALL_GAP - s.d, bx1: wallLength - WALL_GAP });
        }
      }
    }
    return [...own, ...round];
  };
  // What already hangs on each wall, and must be kept clear — starting with
  // every accessory the user put up, whether or not anything hangs from it: a
  // shelving unit does not go in front of a bike hook.
  const hungOn: Partial<Record<PlanFace, WallRect[]>> = {};
  for (const face of PLAN_FACES) {
    for (const section of wallOf(room, face).sections) {
      for (const acc of section.accessories ?? []) {
        const r = accessoryRect(section, acc);
        (hungOn[face] ??= []).push({ x0: r.x, x1: r.x + r.width, y0: r.y, y1: r.y + r.height });
      }
    }
  }
  const inRoom = (r: FloorRect) => r.x0 >= -EPS && r.z0 >= -EPS && r.x1 <= room.length + EPS && r.z1 <= room.width + EPS;
  const faces = [...PLAN_FACES].filter((f) => !wallIsGone(plan, room, f));
  // Walls in a seeded order, so different rooms don't all put the workbench on the front wall.
  const shuffled = <T,>(list: T[]): T[] => {
    const a = [...list];
    for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(random() * (i + 1)); [a[i], a[j]] = [a[j], a[i]]; }
    return a;
  };
  let serial = 0;
  const nextId = (p: { id: string }) => `${roomId}:${p.id}:${++serial}`;

  // The user's accessories in this room, each able to hold one prop; and
  // whether staging may hang accessories of its own (only if the user hung
  // none anywhere in the plan).
  const ownAccessories = !planHasUserAccessories(plan);
  const usedAccessories = new Set<string>();
  // Accessories staging hung itself, by wall, so nothing lands on them twice.
  const stagedAccessoryRects: Partial<Record<PlanFace, WallRect[]>> = {};

  const sameRect = (a: WallRect, b: WallRect) => Math.abs(a.x0 - b.x0) < EPS && Math.abs(a.x1 - b.x1) < EPS && Math.abs(a.y0 - b.y0) < EPS && Math.abs(a.y1 - b.y1) < EPS;
  /** Is the whole hung thing (item + accessory) allowed here on this wall? `host` is the accessory it hangs from (its own keep-out does not count). */
  const hungFits = (face: PlanFace, wall: Surface, rect: WallRect, keep: WallRect[], under: ReturnType<typeof standingOn>, host?: WallRect): boolean => {
    if (rect.y0 < 0.1) return false;                                   // would touch the floor
    if (rect.y1 > wall.height - 0.05) return false;                    // through the ceiling
    if (rect.x0 < 0.05 || rect.x1 > wall.width - 0.05) return false;   // past the wall's end — into the next wall
    if (keep.some((k) => overlapsW(k, rect))) return false;            // a window or door
    if ((hungOn[face] ?? []).some((k) => !(host && sameRect(k, host)) && overlapsW(k, rect))) return false; // something already hung
    // Over (or tight beside) a floor prop's BODY that reaches up to it: no. The room that prop keeps
    // clear beside it is for the floor: a picture may hang in it, a hand's gap off the prop itself.
    if (under.some((s) => s.bx1 + 0.25 > rect.x0 && s.bx0 - 0.25 < rect.x1 && s.h + 0.2 > rect.y0)) return false;
    return true;
  };

  // Floor props' footprints with their heights, and what hangs from the
  // ceiling with how low it comes: each keeps clear of the other.
  const floorBodies: { rect: FloorRect; h: number }[] = [];
  const ceilingBodies: { rect: FloorRect; bottom: number }[] = [];
  const doorways = floorKeepOuts(plan, room);
  const underCeilingProp = (rect: FloorRect, h: number) => ceilingBodies.some((c) => overlaps(c.rect, rect) && h + 0.3 > c.bottom);

  /**
   * Stand a floor prop: against a wall (`anchor: 'wall'`, the usual), out in
   * the room (`'free'`), or either, the seed choosing which to try first
   * (`'any'`). A prop that `faces` another goes in front of that one, facing
   * it, `facesGap` off it, when it is in the room (a woman at the vanity, a
   * sofa across from the TV console) — else as its anchor says. A `centred`
   * prop takes the middle of a wall only. Out in the room a prop faces the
   * middle of the room, never a wall.
   */
  const placeFloor = (prop: PropDef, pick: Pick, skip = 0, key?: string): boolean => {
    const { size, variant } = pick;
    // The draws this placement makes: the room's stream, or (`key`, the main
    // pieces' search) a stream of its own, so the same spots come in the same
    // order every time this prop is tried — and `skip` of the spots that fit
    // are passed over, the next one taken: the search's way of asking for
    // "the n-th spot it could take".
    const draw = key ? rng(key) : random;
    const ordered = <T,>(list: T[]): T[] => shuffleWith(draw, list);
    let seen = 0;
    const take = () => seen++ >= skip;
    // Where the model's box sits in the prop's frame: its centre `offset`
    // [along, into] from the prop's origin. The BODY is the box; the prop's
    // origin (what is saved) is the body's centre less the offset, turned
    // the way the prop faces. With an offset the origin can sit behind the
    // wall, so the box's centre is recorded too (`body`).
    const [ox, oz] = pick.offset ?? [0, 0];
    const side = prop.clearSide ?? 0;
    const back = prop.clearBack ?? 0;
    const off = Math.abs(ox) > EPS || Math.abs(oz) > EPS;
    const stood = (centre: { x: number; z: number }, rotation: number): StagedProp => {
      const o = off ? turned(ox, oz, rotation) : { x: 0, z: 0 };
      countOne(variant);
      return { id: nextId(prop), propId: prop.id, roomId, kind: 'floor', place: 'floor', ...(variant ? { variant } : {}), x: centre.x - o.x, z: centre.z - o.z, ...(off ? { body: centre } : {}), rotation };
    };
    /**
     * Whether a box (w × d × h, keeping `clear` in front, `side` either side,
     * `back` off the wall) fits against `face` with its centre `along` the
     * wall (sheet coordinate), and its footprints when it does. `overSwing`:
     * the room it keeps in front may lie over a door's swing (the body never
     * does) — a pair's last way of standing together before being split.
     */
    const fitAgainst = (face: PlanFace, along: number, w: number, d: number, h: number, clear: number, sideOf: number, backOf: number, overSwing = false) => {
      const gap = WALL_GAP + backOf;
      const body = footprintAgainst(room, face, along, w, d, gap);
      // What it keeps clear: the room in front (clearance), either side (clearSide) — and the side
      // clearance must also lie inside the room, so nothing stands tight into a corner.
      const withClear = footprintAgainst(room, face, along, w + 2 * sideOf, d + clear, gap);
      const withSides = footprintAgainst(room, face, along, w + 2 * sideOf, d, gap);
      if (!inRoom(body) || !inRoom(withSides)) return null;
      if (blockedBy(withClear, h, overSwing)) return null;
      if (overSwing && blockedBy(withSides, h)) return null;
      if (inClearance(withSides)) return null; // in the room another prop keeps in front of it
      if (underCeilingProp(body, h)) return null;
      const [sx0, sx1] = surfaceRange(room, face, along - w / 2, along + w / 2);
      const x0 = Math.min(sx0, sx1), x1 = Math.max(sx0, sx1);
      // Not under something already hung on this wall that it would reach up into (a hanging bike).
      if ((hungOn[face] ?? []).some((hung) => hung.x1 + 0.25 + sideOf > x0 && hung.x0 - 0.25 - sideOf < x1 && h + 0.2 > hung.y0)) return null;
      return { gap, body, withSides, withClear, x0, x1 };
    };
    /**
     * Try to stand the prop against `face` with its box's centre `along` the
     * wall (sheet coordinate). In a corner (`open`: which way along the wall
     * the room is, +1 or -1) it stands tight to the wall beside it, its side
     * clearance kept on the open side only.
     */
    const standAt = (face: PlanFace, along: number, overSwing = false, open?: 1 | -1): boolean => {
      const sideOf = open ? 0 : side;
      const fit = fitAgainst(face, along, size.w, size.d, size.h, prop.clearance ?? 0, sideOf, back, overSwing);
      if (!fit) return false;
      const { gap, body, withSides, withClear, x0, x1 } = fit;
      const strip = open && side > 0 ? footprintAgainst(room, face, along + open * (size.w + side) / 2, side, size.d, gap) : null;
      if (strip && (!inRoom(strip) || blockedBy(strip, size.h) || inClearance(strip))) return false;
      if (!take()) return false;
      if (strip) taken.push(strip);
      taken.push(withSides);
      if ((prop.clearance ?? 0) > 0) clearances.push(withClear);
      floorBodies.push({ rect: body, h: size.h });
      standing.push({ face, x0: x0 - side, x1: x1 + side, bx0: x0, bx1: x1, h: size.h, d: size.d + back });
      againstWall.set(prop.id, { face, along, w: size.w + 2 * side, out: gap + size.d });
      placed.push(stood(fromWall(room, face, along, gap + size.d / 2), rotationFor(face)));
      return true;
    };
    // The prop still to come that stands beside this one (the dryer, for the
    // washer): this one takes a spot with room for it on one side, when there
    // is such a spot. Not while this one stands beside ITS partner (that spot is
    // the partner's call) and not when the mate is not a floor prop of the room.
    const mate = prop.besides && againstWall.has(prop.besides)
      ? undefined
      : kit.props.find((q) => q.id !== prop.id && q.besides === prop.id && q.kind === 'floor' && !q.disabled && !placed.some((p) => p.propId === q.id));
    const roomForMate = (face: PlanFace, along: number, overSwing = false): boolean => {
      if (!mate) return true;
      const mw = mate.size.w, mSide = mate.clearSide ?? 0;
      const apart = (size.w + 2 * side) / 2 + (mate.besidesGap ?? PAIR_GAP) + (mw + 2 * mSide) / 2;
      return [-1, 1].some((s) => fitAgainst(face, along + s * apart, mw, mate.size.d, mate.size.h, mate.clearance ?? 0, mSide, mate.clearBack ?? 0, overSwing) !== null);
    };
    /** Stand the prop with its box centred at (x, z), turned by `rotation`, when the spot is clear. */
    const standFree = (x: number, z: number, facing: number): boolean => {
      // Square to the walls, whichever way it faces: furniture out in a room
      // (an island, a table) stands parallel to a wall, never askew -- the
      // nearest right angle to the way it would face, its box turned with it.
      // A prop that may stand `askew` (a person) faces the middle exactly, and
      // turned off the square its box sweeps a square of its longer side.
      const rotation = prop.askew ? facing : Math.round(facing / (Math.PI / 2)) * (Math.PI / 2);
      const square = Math.abs(Math.sin(2 * rotation)) > EPS;
      const across = !square && Math.abs(Math.sin(rotation)) > 0.5;
      const w = square ? Math.max(size.w, size.d) : across ? size.d : size.w;
      const d = square ? w : across ? size.w : size.d;
      const body: FloorRect = { x0: x - w / 2, x1: x + w / 2, z0: z - d / 2, z1: z + d / 2 };
      const c = Math.max(prop.clearance ?? 0, side);
      const withClear: FloorRect = { x0: body.x0 - c, x1: body.x1 + c, z0: body.z0 - c, z1: body.z1 + c };
      if (!inRoom(body) || blockedBy(withClear, size.h) || inClearance(body) || underCeilingProp(body, size.h)) return false;
      if (!take()) return false;
      taken.push(body);
      if (c > 0) clearances.push(withClear);
      floorBodies.push({ rect: body, h: size.h });
      placed.push(stood({ x, z }, rotation));
      return true;
    };
    const tryWalls = (): boolean => {
      // Beside its partner first (a dryer by the washer): either side of
      // it on the same wall, a hand's gap apart — with its room in front
      // over a door's swing if that is the only way to stand together.
      const partner = prop.besides ? againstWall.get(prop.besides) : undefined;
      if (partner) {
        const sides = ordered([-1, 1]);
        const besideAt = (s: number) => partner.along + s * (partner.w / 2 + (prop.besidesGap ?? PAIR_GAP) + size.w / 2);
        for (const s of sides) if (standAt(partner.face, besideAt(s))) return true;
        for (const s of sides) if (standAt(partner.face, besideAt(s), true)) return true;
      }
      // Every spot on every wall, in the seed's order; with a mate to come, the
      // spots with room for it beside first (then those where the pair's room
      // in front may lie over a door's swing), then (none took) any spot at all:
      // together over a swing beats apart.
      // A `centred` prop (a TV console) takes the middle of a wall or nothing.
      const spots: [PlanFace, number][] = [];
      for (const face of ordered(faces)) {
        const [s0, s1] = wallSpan(room, face);
        const positions: number[] = [];
        if (prop.centred) positions.push((s0 + s1) / 2);
        else for (let along = s0 + size.w / 2 + 0.2; along <= s1 - size.w / 2 - 0.2 + EPS; along += STEP) positions.push(along);
        for (const along of ordered(positions)) spots.push([face, along]);
      }
      if (mate) {
        for (const [face, along] of spots) {
          if (roomForMate(face, along) && standAt(face, along)) return true;
        }
        for (const [face, along] of spots) {
          if (roomForMate(face, along, true) && (standAt(face, along) || standAt(face, along, true))) return true;
        }
      }
      for (const [face, along] of spots) {
        if (standAt(face, along)) return true;
      }
      // No spot keeps its room in front off a door's swing: then the room in
      // front may lie over the swing (the body never does) — a small
      // bathroom's door swings into the space in front of the vanity.
      for (const [face, along] of spots) {
        if (standAt(face, along, true)) return true;
      }
      return false;
    };
    /**
     * In a corner (a shower): the room's corners whose two walls are both
     * there, in the seed's order — the first that fits. Always its back to
     * one wall and its LEFT side to the other (so a model is placed the same
     * way in every corner: a door on its right stays on the open side; Prop
     * Studio's Mirror turns one that is not). Its box's centre is half its
     * width and the hand's gap in from the corner along its back wall; the
     * wall beside it is as tight (footprintAgainst's gap).
     */
    const tryCorner = (): boolean => {
      // Each corner as [its back wall, the wall at its left, whether the corner is at the far end of the back wall's span].
      const corners: [PlanFace, PlanFace, boolean][] = [['front', 'left', false], ['right', 'front', false], ['left', 'back', true], ['back', 'right', true]];
      const order = ordered(corners).filter(([face, beside]) => faces.includes(face) && faces.includes(beside));
      // Its room in front off any door's swing first; failing that, over one (never its body).
      for (const overSwing of [false, true]) {
        for (const [face, , far] of order) {
          const [s0, s1] = wallSpan(room, face);
          const along = far ? s1 - WALL_GAP - size.w / 2 : s0 + WALL_GAP + size.w / 2;
          if (standAt(face, along, overSwing, far ? -1 : 1)) return true;
        }
      }
      return false;
    };
    const tryRoom = (): boolean => {
      // Out in the room: a grid of spots, seeded order, clear of everything, facing the room's middle.
      const spots: [number, number][] = [];
      for (let x = 1 + size.w / 2; x <= room.length - 1 - size.w / 2; x += 1) {
        for (let z = 1 + size.d / 2; z <= room.width - 1 - size.d / 2; z += 1) spots.push([x, z]);
      }
      for (const [x, z] of ordered(spots)) {
        if (standFree(x, z, facingFrom(x, z, room.length / 2, room.width / 2))) return true;
      }
      return false;
    };
    /**
     * In line with a garage door (a car): centred on the door, `inside` of
     * its length in the room from the door's wall and the rest outside
     * through the door, facing in or out as its heading says. One car a
     * door; it must fit through the door and its part in the room must be
     * clear of everything but the door's own path (which is for it).
     */
    const tryBay = (): boolean => {
      const inside = Math.max(0, Math.min(1, prop.inside ?? 1));
      const heading = prop.heading === 'in' || prop.heading === 'out' ? prop.heading : (draw() < 0.5 ? 'in' : 'out');
      for (const bay of ordered(bays)) {
        const { face, opening: o, along } = bay;
        if (usedBays.has(o.id)) continue;
        if (size.w + 2 * side > o.width - 0.2) continue;             // would not fit through the door
        if (inside <= 0) {
          // On the driveway: all of it outside, a step and a half clear of the closed door. Nothing of it is in the room.
          const near = -(DRIVE_STANDOFF + size.d), far = -DRIVE_STANDOFF;
          if (!take()) return false;
          usedBays.add(o.id);
          const centre = fromWall(room, face, along, (near + far) / 2);
          const rotation = rotationFor(face) + (heading === 'out' ? Math.PI : 0);
          placed.push({ ...stood(centre, rotation), bay: { face, openingId: o.id, ...(o.link ? { link: o.link } : {}), inside: 0 } });
          return true;
        }
        const near = WALL_GAP - (1 - inside) * size.d, far = WALL_GAP + inside * size.d;
        // Its part in the room (with its side clearance), and its far end, which must be inside.
        const inRoomPart = footprintAgainst(room, face, along, size.w + 2 * side, far - Math.max(near, 0), Math.max(near, 0));
        const farEnd = footprintAgainst(room, face, along, size.w, 0.01, far - 0.01);
        if (!inRoom(inRoomPart) || !inRoom(farEnd)) continue;
        if (taken.some((t) => t !== bay.keepOut && !sameFloorRect(t, bay.keepOut) && overlaps(t, inRoomPart) && (t.sill === undefined || size.h + 0.2 > t.sill))) continue;
        if (underCeilingProp(inRoomPart, size.h)) continue;
        if (!take()) return false;
        taken.push(inRoomPart);
        floorBodies.push({ rect: inRoomPart, h: size.h });
        const [sx0, sx1] = surfaceRange(room, face, along - size.w / 2, along + size.w / 2);
        standing.push({ face, x0: Math.min(sx0, sx1) - side, x1: Math.max(sx0, sx1) + side, bx0: Math.min(sx0, sx1), bx1: Math.max(sx0, sx1), h: size.h, d: far });
        usedBays.add(o.id);
        const centre = fromWall(room, face, along, (near + far) / 2);
        const rotation = rotationFor(face) + (heading === 'out' ? Math.PI : 0);
        placed.push({ ...stood(centre, rotation), bay: { face, openingId: o.id, ...(o.link ? { link: o.link } : {}), inside } });
        return true;
      }
      return false;
    };
    if (prop.anchor === 'bay') return tryBay();
    // In front of the prop it faces, facing it (so facing that wall), centred
    // on it: `facesGap` in front of it (a sofa a viewing distance from the TV
    // console), or a hand's gap. A room too small for that brings it closer,
    // half a foot at a time, down to the hand's gap (facingMode `near`); in
    // the search's first pass (`full`) only the full distance will do, so the
    // console is moved to where the sofa fits across from it. The floor
    // between them is kept clear: nothing stands between the sofa and the TV.
    const target = prop.faces ? againstWall.get(prop.faces) : undefined;
    if (target) {
      const want = Math.max(FACING_GAP, prop.facesGap ?? FACING_GAP);
      const gaps: number[] = [];
      if (facingMode === 'full') gaps.push(want);
      else {
        for (let g = want; g > FACING_GAP + EPS; g -= 0.5) gaps.push(g);
        gaps.push(FACING_GAP);
      }
      const rotation = rotationFor(target.face) + Math.PI;
      for (const gap of gaps) {
        const body = footprintAgainst(room, target.face, target.along, size.w, size.d, target.out + gap);
        const withClear: FloorRect = { x0: body.x0 - side, x1: body.x1 + side, z0: body.z0 - side, z1: body.z1 + side };
        const lane = gap > FACING_GAP + EPS ? footprintAgainst(room, target.face, target.along, size.w, gap, target.out) : null;
        if (!inRoom(body) || blockedBy(withClear, size.h) || underCeilingProp(body, size.h)) continue;
        if (lane && blockedBy(lane, size.h)) continue;
        if (!take()) continue;
        taken.push(body);
        if (lane) clearances.push(lane);
        floorBodies.push({ rect: body, h: size.h });
        placed.push(stood(fromWall(room, target.face, target.along, target.out + gap + size.d / 2), rotation));
        return true;
      }
      // Nothing in front of it fits: in the search, that is no spot (the other piece moves); else as its anchor says.
      if (facingMode !== 'any') return false;
    }
    const order = prop.anchor === 'free' ? [tryRoom] : prop.anchor === 'corner' ? [tryCorner] : prop.anchor === 'any' ? (draw() < 0.5 ? [tryWalls, tryRoom] : [tryRoom, tryWalls]) : [tryWalls];
    for (const attempt of order) if (attempt()) return true;
    return false;
  };

  /** Everything a floor placement changes, so the main pieces' search can take a placement back. */
  const snapshot = () => ({
    taken: taken.length, clearances: clearances.length, floorBodies: floorBodies.length, standing: standing.length, placed: placed.length, serial,
    againstWall: new Map(againstWall), modelCounts: new Map(modelCounts), usedBays: new Set(usedBays),
  });
  const restore = (s: ReturnType<typeof snapshot>) => {
    taken.length = s.taken; clearances.length = s.clearances; floorBodies.length = s.floorBodies; standing.length = s.standing; placed.length = s.placed; serial = s.serial;
    againstWall.clear(); s.againstWall.forEach((v, k) => againstWall.set(k, v));
    modelCounts.clear(); s.modelCounts.forEach((v, k) => modelCounts.set(k, v));
    usedBays.clear(); s.usedBays.forEach((v) => usedBays.add(v));
  };

  /** Hang a prop from the ceiling, over open floor (the rules above). */
  const placeCeiling = (prop: PropDef, pick: Pick): boolean => {
    const { w, d, h } = pick.size;
    const variant = pick.variant;
    const geo = ceilingGeometry(room);
    const spots: [number, number][] = [];
    for (let x = CEILING_OFF_WALL + w / 2; x <= room.length - CEILING_OFF_WALL - w / 2 + EPS; x += 1) {
      for (let z = CEILING_OFF_WALL + d / 2; z <= room.width - CEILING_OFF_WALL - d / 2 + EPS; z += 1) spots.push([x, z]);
    }
    for (const [x, z] of shuffled(spots)) {
      const body: FloorRect = { x0: x - w / 2, x1: x + w / 2, z0: z - d / 2, z1: z + d / 2 };
      // Held at the lowest the ceiling comes over it, so its top clears a slope.
      const hold = Math.min(geo.heightAt(x, z), geo.heightAt(body.x0, body.z0), geo.heightAt(body.x1, body.z0), geo.heightAt(body.x0, body.z1), geo.heightAt(body.x1, body.z1));
      const bottom = hold - h;
      if (bottom < CEILING_HEADROOM - EPS) continue;
      if (doorways.some((k) => overlaps(k, body))) continue;
      if (floorBodies.some((f) => overlaps(f.rect, body) && f.h + 0.3 > bottom)) continue;
      const spaced: FloorRect = { x0: body.x0 - CEILING_GAP, x1: body.x1 + CEILING_GAP, z0: body.z0 - CEILING_GAP, z1: body.z1 + CEILING_GAP };
      if (ceilingBodies.some((c) => overlaps(c.rect, spaced))) continue;
      ceilingBodies.push({ rect: body, bottom });
      countOne(variant);
      placed.push({ id: nextId(prop), propId: prop.id, roomId, kind: 'ceiling', place: 'ceiling', ...(variant ? { variant } : {}), x, z, rotation: 0, y: hold });
      return true;
    }
    return false;
  };

  /** Hang a prop on a SlatWall accessory of one of `kinds` (best first). */
  const placeHung = (kitProp: PropDef, kinds: SlatWallAccessoryKind[], first: Pick): boolean => {
    if (kinds.length === 0) return false;
    // The model for this wall (the first pick, or another under its limit here); as it reaches, when it has one; its side clearance on top.
    const forWall = (face: PlanFace) => {
      const pick = repickOn(kitProp, first, face);
      if (!pick) return null;
      const prop = hungAs(kitProp, pick);
      const side = prop.clearSide ?? 0;
      return { pick, variant: pick.variant, prop, clear: (r: WallRect): WallRect => ({ ...r, x0: r.x0 - side, x1: r.x1 + side }) };
    };
    // 1. One of the user's, of a kind this prop hangs from, not yet holding anything.
    for (const face of shuffled(faces)) {
      const on = forWall(face);
      if (!on) continue;
      const { variant, prop, clear } = on;
      const wall = wallOf(room, face);
      const keep = wallKeepOuts(wall);
      const under = standingOn(face, wall.width);
      for (const section of slatSections(wall)) {
        for (const acc of section.accessories ?? []) {
          if (usedAccessories.has(acc.id) || !kinds.includes(acc.kind)) continue;
          const def = accessoryDef(acc.kind);
          const r = accessoryRect(section, acc);
          const hx = r.x + def.holdAt.x, hy = r.y + def.holdAt.y;
          const host: WallRect = { x0: r.x, x1: r.x + r.width, y0: r.y, y1: r.y + r.height };
          const rect = clear(hungRect(prop, hx, hy, host, dropOn(prop, acc.kind)));
          if (!hungFits(face, wall, rect, keep, under, host)) continue;
          usedAccessories.add(acc.id);
          (hungOn[face] ??= []).push(rect);
          countOne(variant, face);
          placed.push({ id: nextId(prop), propId: prop.id, roomId, kind: 'slatwall', place: placementOf(acc.kind), ...(variant ? { variant } : {}), x: hx, z: 0, rotation: rotationFor(face), face, y: hy, hostSectionId: section.id, hostAccessoryId: acc.id });
          return true;
        }
      }
    }
    if (!ownAccessories) return false;

    // 2. None of the user's: hang an accessory of our own, on a SlatWall section, on the slot pitch.
    const kind: SlatWallAccessoryKind = kinds[0];
    const def = accessoryDef(kind);
    for (const face of shuffled(faces)) {
      const on = forWall(face);
      if (!on) continue;
      const { variant, prop, clear } = on;
      const wall = wallOf(room, face);
      const keep = wallKeepOuts(wall);
      const under = standingOn(face, wall.width);
      const mine = (stagedAccessoryRects[face] ??= []);
      for (const host of shuffled(slatSections(wall))) {
        const xs: number[] = [];
        for (let x = host.x + 0.2; x + def.size.w <= host.x + host.width - 0.2 + EPS; x += STEP) xs.push(x);
        // Its flanges seated in the section's grooves, the highest seat first.
        const seats = seatPositions(host, kind).map((y) => host.y + y);
        for (const x of shuffled(xs)) {
          for (const ay of seats) {
            const accR: WallRect = { x0: x, x1: x + def.size.w, y0: ay, y1: ay + def.size.h };
            if (mine.some((m) => overlapsW(m, accR))) continue;
            const hx = x + def.holdAt.x, hy = ay + def.holdAt.y;
            const rect = clear(hungRect(prop, hx, hy, accR, dropOn(prop, kind)));
            if (!hungFits(face, wall, rect, keep, under)) continue;
            mine.push(accR);
            (hungOn[face] ??= []).push(rect);
            countOne(variant, face);
            const accessoryId = nextId({ id: kind });
            placed.push({ id: accessoryId, propId: kind, roomId, kind: 'accessory', x, z: 0, rotation: rotationFor(face), face, y: ay, hostSectionId: host.id, accessoryKind: kind });
            placed.push({ id: nextId(prop), propId: prop.id, roomId, kind: 'slatwall', place: placementOf(kind), ...(variant ? { variant } : {}), x: hx, z: 0, rotation: rotationFor(face), face, y: hy, hostSectionId: host.id, hostAccessoryId: accessoryId });
            return true;
          }
        }
      }
    }
    return false;
  };

  /**
   * Is a wall rect on product all the way up: covered by sections that each
   * span its width, stacked with no more than a trim's gap between them?
   */
  const onProduct = (sections: Surface['sections'], rect: WallRect): boolean => {
    const spans = sections
      .filter((s) => s.x <= rect.x0 + EPS && s.x + s.width >= rect.x1 - EPS && s.y < rect.y1 && s.y + s.height > rect.y0)
      .sort((a, b) => a.y - b.y);
    let y = rect.y0;
    for (const s of spans) {
      if (s.y > y + SEAM_GAP) return false;
      y = Math.max(y, s.y + s.height);
      if (y >= rect.y1 - EPS) return true;
    }
    return y >= rect.y1 - EPS;
  };

  /**
   * A wall prop (a towel bar, wall art): on the wall's product, at its mount
   * height, off openings — and PLACED WITH INTENT: centred in the free run of
   * wall at its height, between the wall's ends and whatever is in the way
   * there (a window, a door, a shower standing against the wall, something
   * already hung); `want` copies are spread evenly along one run (two pictures
   * at a third and two thirds), the rest going to the next run or wall. The
   * walls are tried in the seed's order, the widest run of each first. It may
   * reach across the seam onto the section above or below (onProduct); its
   * host is the section at the height it is held. Returns how many were placed.
   */
  const placeWall = (kitProp: PropDef, first: Pick, want = 1): number => {
    let done = 0;
    const order = shuffled(faces);
    // Two passes: first a wall with a run that holds ALL the copies still wanted (two pictures together, on the
    // long wall), then any wall for what is left, one run at a time.
    for (const together of [true, false]) for (const face of order) {
      if (done >= want) break;
      // The model for this wall (the first pick, or another under its limit here); as it reaches either side of the bar and hangs below it.
      const pick = repickOn(kitProp, first, face);
      if (!pick) continue;
      const variant = pick.variant;
      const prop = hungAs(kitProp, pick);
      const wall = wallOf(room, face);
      const keep = wallKeepOuts(wall);
      const under = standingOn(face, wall.width);
      // Held at its mount height: a prop with a `drop` (a towel bar's towel)
      // hangs that far below the held point and reaches the rest above it.
      const y = prop.mountHeight ?? 4;
      const drop = prop.drop ?? 0;
      const y0 = y - drop, y1 = y + prop.size.h - drop;
      // The hold point `holdX` right of the box's centre: the box reaches that much further left of it; its side clearance on top.
      const side = prop.clearSide ?? 0;
      const left = (prop.reach?.left ?? prop.size.w / 2 + (prop.holdX ?? 0)) + side;
      const right = (prop.reach?.right ?? prop.size.w / 2 - (prop.holdX ?? 0)) + side;
      const width = left + right;
      // What is in the way at that height, as runs along the wall (hungFits's own margins on each) …
      const blocks: [number, number][] = [
        ...keep.filter((k) => k.y0 < y1 && k.y1 > y0).map((k): [number, number] => [k.x0, k.x1]),
        ...(hungOn[face] ?? []).filter((k) => k.y0 < y1 && k.y1 > y0).map((k): [number, number] => [k.x0, k.x1]),
        ...under.filter((s) => s.h + 0.2 > y0).map((s): [number, number] => [s.bx0 - 0.25, s.bx1 + 0.25]),
      ].sort((a, b) => a[0] - b[0]);
      // … and the free runs between them, from the wall's ends, widest first.
      const runs: [number, number][] = [];
      let at = WALL_END_MARGIN;
      for (const [b0, b1] of blocks) {
        if (b0 > at) runs.push([at, b0]);
        at = Math.max(at, b1);
      }
      if (wall.width - WALL_END_MARGIN > at) runs.push([at, wall.width - WALL_END_MARGIN]);
      runs.sort((a, b) => (b[1] - b[0]) - (a[1] - a[0]));
      const hostAt = (x: number) => wall.sections.find((s) => s.y <= y + EPS && y <= s.y + s.height + EPS && s.x <= x + EPS && x <= s.x + s.width + EPS)
        ?? wall.sections.find((s) => s.y < y1 && s.y + s.height > y0 && s.x <= x + EPS && x <= s.x + s.width + EPS);
      // The hold point for a box centred at c, and whether that spot is sound.
      const holdAt = (c: number) => c + (left - right) / 2;
      const fits = (x: number): boolean => {
        const rect: WallRect = { x0: x - left, x1: x + right, y0, y1 };
        return onProduct(wall.sections, rect) && hungFits(face, wall, rect, keep, under) && !!hostAt(x);
      };
      const commit = (x: number) => {
        const rect: WallRect = { x0: x - left, x1: x + right, y0, y1 };
        (hungOn[face] ??= []).push(rect);
        countOne(variant, face);
        placed.push({ id: nextId(prop), propId: prop.id, roomId, kind: 'wall', place: 'wall', ...(variant ? { variant } : {}), x, z: 0, rotation: rotationFor(face), face, y, hostSectionId: hostAt(x)!.id });
        done++;
      };
      for (const [r0, r1] of runs) {
        if (done >= want) break;
        const free = r1 - r0;
        if (free < width) continue;
        // As many of the copies still wanted as the run holds evenly spaced, the fewer when they would crowd.
        const most = Math.min(want - done, MAX_SPREAD);
        for (let n = most; n >= (together ? most : 1); n--) {
          const step = free / (n + 1);
          if (n > 1 && step < width + SPREAD_GAP) continue;
          const xs = Array.from({ length: n }, (_, i) => holdAt(r0 + step * (i + 1)));
          if (!xs.every(fits)) continue;
          xs.forEach(commit);
          break;
        }
      }
    }
    return done;
  };

  // In order — a car in its bay before anything else, whatever its order: the rest of the room keeps out of its lane.
  // Which vehicle gets the first door is the seed's call (its own draw, so the rest of the room's stays as it was):
  // a one-door garage gets the car or the truck, not always the one listed first.
  const bayRank = new Map<string, number>();
  const bayDraw = rng(`${options.seed ?? 'stage'}:${roomId}:bays`);
  kit.props.filter((p) => p.anchor === 'bay').map((p) => ({ p, k: bayDraw() })).sort((a, b) => a.k - b.k).forEach(({ p }, i) => bayRank.set(p.id, i));
  // A main piece that found no room last time (stageRoom `early`) goes right after the cars, in the order they were promoted.
  const rank = (p: PropDef) => (p.anchor === 'bay' ? -1000 + (bayRank.get(p.id) ?? 0) : early.includes(p.id) ? -500 + early.indexOf(p.id) : p.order ?? 50);
  const byRank = [...kit.props].sort((a, b) => rank(a) - rank(b));
  // A prop that stands beside another, or faces it, goes right after it,
  // whatever its order: its partner must stand first (the sofa, ordered
  // before the TV console it faces, waits for the console). Two that stand
  // beside (or face) each other go in their own order, the first of them
  // where its order says — neither waits for the other.
  const props: PropDef[] = [];
  const done = new Set<string>();
  const waiting = new Map<string, PropDef[]>();
  const pendingFor = (p: PropDef): PropDef | undefined => {
    const partner = p.besides && p.besides !== p.id ? byRank.find((o) => o.id === p.besides) : undefined;
    if (partner && partner.besides !== p.id && !done.has(partner.id)) return partner;
    const target = p.faces && p.faces !== p.id ? byRank.find((o) => o.id === p.faces) : undefined;
    if (target && target.faces !== p.id && !done.has(target.id)) return target;
    return undefined;
  };
  const admit = (p: PropDef): void => {
    const dep = pendingFor(p);
    if (dep) { waiting.set(dep.id, [...(waiting.get(dep.id) ?? []), p]); return; }
    props.push(p);
    done.add(p.id);
    const queued = waiting.get(p.id) ?? [];
    waiting.delete(p.id);
    for (const q of queued) admit(q);
  };
  for (const p of byRank) admit(p);
  for (const rest of waiting.values()) props.push(...rest);
  const density = Math.max(0, Math.min(MAX_DENSITY, options.density ?? 1));

  // The MAIN PIECES first (floor props ordered below MAIN_ORDER: the vanity,
  // the toilet, a shower; a car in its bay is handled on its own), by a
  // search rather than one greedy pass: each piece is tried at every spot it
  // can take (and as each of its models), in its own seeded order, and when
  // a later piece finds no room the earlier one is moved — so when the room
  // has a way to seat them all, it is found. A bathroom's toilet standing
  // mid-wall, closing the shower's only corner, is moved along. Bounded by
  // SEARCH_BUDGET placements; past that (or with no way at all) they are
  // placed greedily, like everything else.
  const mainItems = props.filter((p) => p.kind === 'floor' && !p.disabled && p.anchor !== 'bay' && (p.order ?? 50) < MAIN_ORDER && placesOf(p)[0] === 'floor')
    .flatMap((p) => Array.from({ length: typeof p.count === 'number' ? p.count : 1 }, (_, copy) => ({ prop: p, copy })));
  const mainIds = new Set(mainItems.map((m) => m.prop.id));
  const seedKey = `${options.seed ?? 'stage'}:${roomId}`;
  const searchPick = (prop: PropDef, v: StageVariant | undefined): Pick => (v ? { size: v.size, variant: v.key, offset: v.offset } : { size: prop.size });
  let budget = SEARCH_BUDGET;
  const solve = (i: number): boolean => {
    if (i === mainItems.length) return true;
    const { prop, copy } = mainItems[i];
    const key = `${seedKey}:${prop.id}:${copy}`;
    const list = options.variants?.[prop.id];
    const pool: (StageVariant | undefined)[] = list?.length ? shuffleWith(rng(`${key}:models`), list).filter((v) => underCap(v)) : [undefined];
    if (pool.length === 0) return solve(i + 1); // every model at its limit: this copy is left out
    for (const v of pool) {
      for (let n = 0; ; n++) {
        if (budget-- <= 0) return false;
        const snap = snapshot();
        if (!placeFloor(prop, searchPick(prop, v), n, key)) break; // no n-th spot for this model
        if (solve(i + 1)) return true;
        restore(snap);
      }
    }
    return false;
  };
  // Run where the first main piece comes in the order: what is ordered before
  // them (a bike on every bike hook, order 5) is up first, as it always was.
  const seatMainPieces = () => {
    const start = snapshot();
    // A piece that faces another (the sofa, the console) at its full distance first; then nearer, when
    // no arrangement allows that; then (nothing seats them all) each where it can go, the rest left out.
    const modes: ('full' | 'near')[] = mainItems.some((m) => m.prop.faces) ? ['full', 'near'] : ['full'];
    for (const mode of modes) {
      facingMode = mode;
      budget = SEARCH_BUDGET;
      if (solve(0)) { facingMode = 'any'; return; }
      restore(start);
    }
    facingMode = 'any';
    for (const { prop, copy } of mainItems) {
      const key = `${seedKey}:${prop.id}:${copy}`;
      const list = options.variants?.[prop.id];
      const pool = list?.length ? shuffleWith(rng(`${key}:models`), list).filter((v) => underCap(v)) : [undefined];
      for (const v of pool) if (placeFloor(prop, searchPick(prop, v), 0, key)) break;
    }
  };
  let mainSeated = false;

  for (const prop of props) {
    if (prop.disabled) continue;        // out of the pool (Prop Studio)
    if (prop.kind === 'yard') continue; // outside the house: stageYard, once per plan
    if (mainIds.has(prop.id)) {         // a main piece: all of them seated together by the search, at the first one's turn
      if (!mainSeated) { mainSeated = true; seatMainPieces(); }
      continue;
    }
    // Not wanted once its stand-in is there (no bike on the floor when one hangs).
    if (prop.unless && placed.some((p) => p.propId === prop.unless)) continue;
    // 'each': one for every accessory of the user's it can hang from (one, when staging hangs its own).
    const kitCount = prop.count === 'each'
      ? Math.max(1, faces.flatMap((f) => wallOf(room, f).sections.flatMap((s) => s.accessories ?? [])).filter((a) => prop.hangsOn?.includes(a.kind)).length)
      : prop.count ?? 1;
    // The density thins or multiplies all but the main pieces (a car in its bay is one, and so is any prop whose count is `fixed`); a fraction of a copy is the seed's call.
    let count = kitCount;
    if (!prop.fixed && prop.anchor !== 'bay' && (prop.order ?? 50) >= MAIN_ORDER && density !== 1) {
      const want = kitCount * density;
      count = Math.floor(want) + (random() < want - Math.floor(want) ? 1 : 0);
    }
    // A wall prop's copies go up together, spread along the wall (placeWall).
    if (prop.kind === 'wall') {
      const pick = pickFor(prop);
      if (pick && count > 0) placeWall(prop, pick, count);
      continue;
    }
    for (let n = 0; n < count; n++) {
      // Which of its models this copy is (and so how big it is), before any place is tried:
      // a floor or ceiling prop's among the models still under their limit in this room
      // (a hung one is picked again per wall, in placeHung); none left: no copy.
      const pick = pickFor(prop, prop.kind === 'floor' || prop.kind === 'ceiling' ? (v) => underCap(v) : undefined);
      if (!pick) continue;
      // Each way it can go, best first, until one fits; a run of accessories
      // is tried together (the user's of any of them, else staging's own of the first).
      const places: Placement[] = placesOf(prop);
      for (let i = 0; i < places.length;) {
        const p = places[i];
        let ok: boolean;
        if (p === 'floor') { ok = placeFloor(prop, pick); i++; }
        else if (p === 'ceiling') { ok = placeCeiling(prop, pick); i++; }
        else {
          const kinds: SlatWallAccessoryKind[] = [];
          while (i < places.length && isAccessoryPlace(places[i])) kinds.push(PLACEMENT_ACCESSORY[places[i++]] as SlatWallAccessoryKind);
          ok = placeHung(prop, kinds, pick);
        }
        if (ok) break;
      }
    }
  }
  return placed;
}

// The yard's props keep off things by this much (feet).
const TREE_OFF_HOUSE = 8;
const TREE_OFF_SLAB = 4;
const TREE_APART = 6;
const SHRUB_OFF_WALL = 1.5;

/**
 * Stage the yard: the kit's `yard` props outside the house (kits.ts YARD) —
 * trees out on the lawn, on the spots the most windows look at, and a shrub
 * under each exterior window it fits beneath — clear of the house, the
 * slabs and each other (plan/yard.ts says where those are). Each is
 * recorded in the coordinates of the room nearest it (a shrub's window's
 * room) — where the viewer draws that room's props — and so lies outside
 * 0..L / 0..W of it. Seeded like the rooms; the density thins and
 * multiplies as it does inside.
 */
export function stageYard(plan: FloorPlan, kit: StagingKit, options: StageOptions = {}): StagedProp[] {
  if (plan.rooms.length === 0) return [];
  const yard = yardOf(plan);
  const lawn = yard.lawn;
  const random = rng(`${options.seed ?? 'stage'}:yard`);
  const density = Math.max(0, Math.min(MAX_DENSITY, options.density ?? 1));
  const placed: StagedProp[] = [];
  let serial = 0;
  const grown = (r: FloorRect, by: number): FloorRect => ({ x0: r.x0 - by, z0: r.z0 - by, x1: r.x1 + by, z1: r.z1 + by });
  // What is taken outside (sheet coordinates): the slabs, the house and a hand round it, and what is placed here.
  const taken: FloorRect[] = [
    ...yard.slabs.map((s) => ({ x0: s.x0, z0: s.z0, x1: s.x1, z1: s.z1 })),
    ...yard.footprints.map((f) => grown(f, 0.5)),
  ];
  const windows = yard.openings.filter((o) => o.kind === 'window');
  const usedWindows = new Set<number>();
  const inLawn = (r: FloorRect) => r.x0 >= lawn.x0 && r.z0 >= lawn.z0 && r.x1 <= lawn.x1 && r.z1 <= lawn.z1;
  const nearestRoom = (x: number, z: number): PlanRoom => {
    let best = plan.rooms[0], bestD = Infinity;
    for (const r of plan.rooms) {
      const d = Math.hypot(Math.max(r.x - x, 0, x - (r.x + r.length)), Math.max(r.z - z, 0, z - (r.z + r.width)));
      if (d < bestD) { bestD = d; best = r; }
    }
    return best;
  };
  const pick = (p: PropDef): Pick => {
    const list = options.variants?.[p.id];
    if (!list?.length) return { size: p.size };
    const v = list[Math.min(list.length - 1, Math.floor(random() * list.length))];
    return { size: v.size, variant: v.key, offset: v.offset };
  };
  const record = (prop: PropDef, p: Pick, room: PlanRoom, centre: { x: number; z: number }, rotation: number): StagedProp => {
    const [ox, oz] = p.offset ?? [0, 0];
    const off = Math.abs(ox) > EPS || Math.abs(oz) > EPS;
    const o = off ? turned(ox, oz, rotation) : { x: 0, z: 0 };
    const local = { x: centre.x - room.x, z: centre.z - room.z };
    return { id: `yard:${prop.id}:${++serial}`, propId: prop.id, roomId: room.id, kind: 'yard', place: 'yard', ...(p.variant ? { variant: p.variant } : {}), x: local.x - o.x, z: local.z - o.z, ...(off ? { body: local } : {}), rotation };
  };
  /** How many exterior windows look at a sheet point: in the window's field, 8 to 35 ft out. */
  const looked = (x: number, z: number): number => windows.filter((w) => {
    const along = w.axis === 'x' ? x : z, across = w.axis === 'x' ? z : x;
    const out = (across - w.at) * (w.axis === 'x' ? w.outward.z : w.outward.x);
    if (out < 8 || out > 35) return false;
    return Math.abs(along - (w.from + w.to) / 2) <= (w.to - w.from) / 2 + 0.6 * out;
  }).length;
  const props = kit.props.filter((p) => p.kind === 'yard' && !p.disabled).sort((a, b) => (a.order ?? 50) - (b.order ?? 50));
  for (const prop of props) {
    const kitCount = prop.count === 'each' ? windows.length : prop.count ?? 1;
    let count = kitCount;
    if (!prop.fixed && density !== 1) {
      const want = kitCount * density;
      count = Math.floor(want) + (random() < want - Math.floor(want) ? 1 : 0);
    }
    for (let n = 0; n < count; n++) {
      const p = pick(prop);
      // A tree stands by its trunk, not its crown: its model's size says how
      // it is drawn, but the ground it takes is the kit's (a crown overhangs
      // the lawn's edge, the driveway, as real ones do) -- else a life-size
      // tree model, 70 ft across, could stand nowhere. The crown must still
      // clear the HOUSE, though: a canopy through a wall is in the room.
      const size = prop.anchor === 'lawn' ? { w: Math.min(p.size.w, prop.size.w), d: Math.min(p.size.d, prop.size.d), h: p.size.h } : p.size;
      const crown = { w: Math.max(size.w, p.size.w), d: Math.max(size.d, p.size.d) };
      let done = false;
      if (prop.anchor === 'window') {
        // Under a window, whatever its height (a bush may well reach over a sill), a step off the wall, turned to the house.
        for (const i of shuffleWith(random, windows.map((_, k) => k))) {
          const w = windows[i];
          if (usedWindows.has(i)) continue;
          const mid = (w.from + w.to) / 2, out = SHRUB_OFF_WALL + size.d / 2;
          const centre = w.axis === 'x' ? { x: mid, z: w.at + w.outward.z * out } : { x: w.at + w.outward.x * out, z: mid };
          const hw = (w.axis === 'x' ? size.w : size.d) / 2, hd = (w.axis === 'x' ? size.d : size.w) / 2;
          const body: FloorRect = { x0: centre.x - hw, x1: centre.x + hw, z0: centre.z - hd, z1: centre.z + hd };
          if (!inLawn(body) || taken.some((t) => overlaps(t, body))) continue;
          usedWindows.add(i);
          taken.push(body);
          placed.push(record(prop, p, findRoom(plan, w.roomId) ?? nearestRoom(centre.x, centre.z), centre, rotationFor(w.face)));
          done = true;
          break;
        }
      } else {
        // Out on the lawn: a grid of spots, clear of the house, the slabs and the others; the spots the most windows look at first.
        const spots: [number, number][] = [];
        for (let x = lawn.x0 + 2 + size.w / 2; x <= lawn.x1 - 2 - size.w / 2; x += 2) {
          for (let z = lawn.z0 + 2 + size.d / 2; z <= lawn.z1 - 2 - size.d / 2; z += 2) spots.push([x, z]);
        }
        const scored = shuffleWith(random, spots).map((s) => ({ s, n: looked(s[0], s[1]) })).sort((a, b) => b.n - a.n);
        const yaw = random() * Math.PI * 2;
        // How far a spot is from the nearest wall of the house (the trunk to the footprint's edge).
        const offHouse = (x: number, z: number) => Math.min(...yard.footprints.map((f) => Math.max(f.x0 - x, x - f.x1, f.z0 - z, z - f.z1, 0)));
        // First the spots where the whole crown stays a foot clear of every
        // wall (a canopy through a wall is in the room); when a model draws
        // too wide for any (a life-size tree on a small lawn), the spots
        // farthest from the house, so as little of it as can be comes near.
        const clearFirst = [...scored.filter(({ s: [x, z] }) => {
          const canopy: FloorRect = { x0: x - crown.w / 2, x1: x + crown.w / 2, z0: z - crown.d / 2, z1: z + crown.d / 2 };
          return !yard.footprints.some((f) => overlaps(grown(f, 1), canopy));
        }), ...scored.map(({ s }) => ({ s, n: offHouse(s[0], s[1]) })).sort((a, b) => b.n - a.n)];
        for (const { s: [x, z] } of clearFirst) {
          const body: FloorRect = { x0: x - size.w / 2, x1: x + size.w / 2, z0: z - size.d / 2, z1: z + size.d / 2 };
          if (yard.footprints.some((f) => overlaps(grown(f, TREE_OFF_HOUSE), body))) continue;
          if (yard.slabs.some((f) => overlaps(grown(f, TREE_OFF_SLAB), body))) continue;
          if (taken.some((t) => overlaps(t, grown(body, TREE_APART / 2)))) continue;
          taken.push(grown(body, TREE_APART / 2));
          placed.push(record(prop, p, nearestRoom(x, z), { x, z }, yaw));
          done = true;
          break;
        }
      }
      if (!done) break; // nowhere left for this prop: no more of it
    }
  }
  return placed;
}

/** Stage every room of a plan (the kit of the plan's space type), and the yard round them. */
export function stagePlan(plan: FloorPlan, kit: StagingKit, options: StageOptions = {}): StagedProp[] {
  return [...plan.rooms.flatMap((room) => stageRoom(plan, room.id, kit, options)), ...stageYard(plan, kit, options)];
}

export { type FloorRect as StagingFloorRect };
